import { CalendarIcon } from "@phosphor-icons/react";
import * as React from "react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const formatDate = (date: Date, locale?: string) =>
  date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

type DatePickerProps = {
  value?: Date;
  onValueChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Render the trigger with the destructive border. Use for form-level
   * error states — pairs with `<FieldError />` in a Field.
   */
  invalid?: boolean;
  locale?: string;
  align?: "start" | "center" | "end";
  className?: string;
  triggerClassName?: string;
};

function DatePicker({
  value,
  onValueChange,
  placeholder = "Pick a date",
  disabled,
  invalid,
  locale,
  align = "start",
  className,
  triggerClassName,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-invalid={invalid || undefined}
            data-slot="date-picker-trigger"
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-4 text-sm font-normal shadow-xs transition-colors outline-none",
              "hover:bg-accent hover:text-accent-foreground",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-invalid:border-destructive",
              value ? "text-foreground" : "text-muted-foreground",
              triggerClassName,
              className,
            )}
          >
            <CalendarIcon className="size-4 shrink-0" />
            <span className="flex-1 truncate text-left">
              {value ? formatDate(value, locale) : placeholder}
            </span>
          </button>
        }
      />
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onValueChange?.(date);
            if (date) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker, type DatePickerProps };
